import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  CircularProgress,
  Typography,
  Tooltip,
  Tabs,
  Tab,
  Radio,
} from "@mui/material";
import { Help } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { gateway, refreshGateway } from "../gateway";
import { updateConnectBusinessState, nextViewConnectBusinessState } from "../redux/features/connectBusinessStateSlice";
import { updateActiveCompany, updateUser } from "../redux/features/userStateSlice";
import { SnackbarAlert } from "./common/SnackbarAlert";
import { AccountDeactivation } from "./informationBtnBolean";
import { AddressAutocomplete } from "./AddressAutoComplete";
import { PhoneInputWithFlags } from "./common/PhoneInputWithFlags";
import { EditableAccountAvatar } from "./common/AccountAvatar";
import { colors } from "./ui/colors";

const formatPhone = (value) => {
  if (!value) return "";
  let clean = String(value).replace(/\D/g, "");
  if (clean.length === 11 && clean.startsWith("1")) {
    clean = clean.slice(1);
  }
  clean = clean.slice(0, 10);
  if (clean.length >= 7) return `(${clean.slice(0, 3)}) ${clean.slice(3, 6)}-${clean.slice(6)}`;
  if (clean.length >= 4) return `(${clean.slice(0, 3)}) ${clean.slice(3)}`;
  return clean ? `(${clean}` : "";
};

const cleanPhone = (value) => {
  if (!value) return "";
  let clean = String(value).replace(/\D/g, "");
  if (clean.length === 11 && clean.startsWith("1")) {
    clean = clean.slice(1);
  }
  return clean.slice(0, 10);
};

const normalizeCountryCode = (value) => {
  if (!value) return "US";
  if (value === "USA-1" || value === "USA") return "US";
  return value;
};

const formatEmployeeId = (value) => {
  const clean = value?.replace(/[^0-9]/g, "") || "";
  return clean.length > 3 ? `${clean.slice(0, 2)}-${clean.slice(2)}` : clean;
};

const MAX_PROFILE_IMAGE_SIZE = 2 * 1024 * 1024;
const MAX_PROFILE_IMAGE_DIMENSION = 1600;
const PROFILE_IMAGE_QUALITY_STEPS = [0.9, 0.8, 0.7, 0.6, 0.5];

const loadImageElement = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load image"));
    image.src = src;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error("Unable to compress image"));
      },
      type,
      quality,
    );
  });

const getScaledDimensions = (width, height, maxDimension) => {
  if (Math.max(width, height) <= maxDimension) {
    return { width, height };
  }

  if (width >= height) {
    return {
      width: maxDimension,
      height: Math.round((height / width) * maxDimension),
    };
  }

  return {
    width: Math.round((width / height) * maxDimension),
    height: maxDimension,
  };
};

const compressProfileImage = async (file, maxSize = MAX_PROFILE_IMAGE_SIZE) => {
  if (!file?.type?.startsWith("image/")) {
    throw new Error("Selected file is not a valid image");
  }

  if (file.size <= maxSize) {
    return file;
  }

  const sourceUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageElement(sourceUrl);
    const { width, height } = getScaledDimensions(image.width, image.height, MAX_PROFILE_IMAGE_DIMENSION);
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Unable to process image");
    }

    canvas.width = width;
    canvas.height = height;
    context.drawImage(image, 0, 0, width, height);

    const outputTypes = file.type === "image/png" ? ["image/png", "image/jpeg"] : [file.type || "image/jpeg", "image/jpeg"];

    for (const outputType of outputTypes) {
      for (const quality of PROFILE_IMAGE_QUALITY_STEPS) {
        const blob = await canvasToBlob(canvas, outputType, quality);
        if (blob.size <= maxSize) {
          const extension = outputType === "image/png" ? "png" : outputType === "image/webp" ? "webp" : "jpg";
          const baseName = file.name.replace(/\.[^/.]+$/, "") || "profile";
          return new File([blob], `${baseName}.${extension}`, { type: outputType });
        }
      }
    }
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }

  throw new Error("Image is too large. Please choose a smaller image.");
};

const withCacheBuster = (url) => {
  if (!url) return url;

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}updatedAt=${Date.now()}`;
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    paddingX: "4px",
    paddingY: "4px",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    "& fieldset": {
      borderColor: colors.orange.base,
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: colors.orange[200],
    },
    "&.Mui-focused fieldset": {
      borderColor: colors.orange.base,
      boxShadow: `0 0 0 2px ${colors.orange[200]}33`,
    },
    "&.Mui-error fieldset": {
      borderColor: colors.danger.base,
    },
    "&.Mui-error.Mui-focused fieldset": {
      borderColor: colors.danger[700],
      boxShadow: `0 0 0 2px ${colors.danger[200]}55`,
    },
  },
  "& .MuiInputLabel-root": {
    color: colors.gray[400],
    fontWeight: 500,
    "&.Mui-focused": {
      color: colors.orange.base,
    },
    "&.Mui-error": {
      color: colors.danger.base,
    },
  },
  "& input": {
    padding: "12px 14px",
    fontSize: "1.0rem",
    color: colors.gray.base,
  },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    minHeight: 40,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.orange.base,
    borderWidth: "1px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.orange[200],
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.orange.base,
    boxShadow: `0 0 0 2px ${colors.orange[200]}33`,
  },
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.danger.base,
  },
  "& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.danger[700],
    boxShadow: `0 0 0 2px ${colors.danger[200]}55`,
  },
  "& .MuiSelect-select": {
    padding: "12px 14px",
    fontSize: "1rem",
    lineHeight: "1.4",
    display: "flex",
    alignItems: "center",
    color: colors.gray.base,
  },
  "& .MuiSvgIcon-root": {
    color: colors.orange.base,
  },
};

const compactInputSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    borderRadius: "8px",
    paddingX: "2px",
    paddingY: "1px",
  },
  "& input": {
    ...inputSx["& input"],
    padding: "8px 12px",
    fontSize: "0.9rem",
  },
};

export const ConnectBusinessAccount = () => {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      alias: "",
      email: "",
      phone: "",
      countryCode: "US",
      companyName: "",
      employerId: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
    mode: "onChange",
  });

  const userState = useSelector((state) => state.userState);

  const reduxDispatch = useDispatch();
  const tokenLocal = sessionStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const [tabValue, setTabValue] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedImagePreviewUrl, setSelectedImagePreviewUrl] = useState("");
  const [isCompressingImage, setIsCompressingImage] = useState(false);
  const [displayNameMode, setDisplayNameMode] = useState("fullName");

  const watchedValues = watch();
  const fullNamePreview = [watchedValues.firstName, watchedValues.lastName].filter(Boolean).join(" ").trim();
  const aliasPreview = String(watchedValues.alias || "").trim();
  const displayNamePreview = displayNameMode === "alias" ? aliasPreview || "Alias" : fullNamePreview || "First Last";

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    if (isDirty) {
      reduxDispatch(updateConnectBusinessState(watchedValues));
    }
  }, [watchedValues, isDirty, reduxDispatch]);

  useEffect(() => {
    return () => {
      if (selectedImagePreviewUrl) {
        URL.revokeObjectURL(selectedImagePreviewUrl);
      }
    };
  }, [selectedImagePreviewUrl]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const response = await gateway.get("/users/self", {
        headers: {
          Authorization: `${tokenLocal}`,
        },
      });
      const data = response.data?.data ?? {};
      setDisplayNameMode(data.useAliasAsDisplayName ? "alias" : "fullName");
      const rawPhone = data.phone ?? data.phoneNumber ?? userState.phone ?? userState.phoneNumber ?? "";
      const phoneValue = formatPhone(rawPhone);
      const rawCountryCode = data.countryCode ?? userState.countryCode ?? "US";
      const countryCodeValue = normalizeCountryCode(rawCountryCode);
      const companyData = data.companies?.[0] ?? userState.company ?? {};

      reset(
        {
          firstName: data.originalFirstName ?? data.firstName ?? userState.firstName ?? "",

          lastName: data.useAliasAsDisplayName ? (data.originalLastName ?? "") : (data.lastName ?? userState.lastName ?? ""),

          alias: data.alias ?? userState.alias ?? "",
          email: data.email ?? userState.email ?? "",
          phone: phoneValue,
          countryCode: countryCodeValue,
          companyName: companyData.businessName ?? "",
          employerId: companyData.employerId ?? "",
          city: companyData.city ?? data.city ?? userState.company?.city ?? "",
          state: companyData.state ?? data.state ?? userState.company?.state ?? "",
          zipCode: companyData.zipCode ?? data.zipCode ?? userState.company?.zipCode ?? "",
          country: companyData.country ?? data.country ?? userState.company?.country ?? "",
        },
        { keepTouched: false },
      );
      reduxDispatch(
        updateUser({
          firstName: data.firstName ?? userState.firstName ?? "",
          lastName: data.lastName ?? userState.lastName ?? "",
          alias: data.alias ?? userState.alias ?? "",
          displayName: data.displayName ?? "",
          useAliasAsDisplayName: Boolean(data.useAliasAsDisplayName),
          email: data.email ?? userState.email ?? "",
          phone: data.phone ?? cleanPhone(rawPhone),
          phoneNumber: data.phoneNumber ?? data.phone ?? cleanPhone(rawPhone),
          countryCode: countryCodeValue,
          country: data.country ?? userState.country ?? "",
          address: data.address ?? userState.address ?? "",
          avatar: data.avatar ? withCacheBuster(data.avatar) : userState.avatar,
        }),
      );
      reduxDispatch(
        updateActiveCompany({
          businessName: companyData.businessName ?? "",
          employerId: companyData.employerId ?? "",
          city: companyData.city ?? data.city ?? "",
          state: companyData.state ?? data.state ?? "",
          zipCode: companyData.zipCode ?? data.zipCode ?? "",
          country: companyData.country ?? data.country ?? "",
          address: companyData.address ?? data.address ?? "",
        }),
      );
    } catch (error) {
      showAlert("Error loading user data", "error");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
  };

  const handleDisplayNameModeChange = (mode) => {
    setDisplayNameMode(mode);
  };

  const handleAccountAvatarFile = async (file) => {
    if (!file) {
      return;
    }

    try {
      setIsCompressingImage(true);
      const processedFile = await compressProfileImage(file);
      const previewUrl = URL.createObjectURL(processedFile);

      if (selectedImagePreviewUrl) {
        URL.revokeObjectURL(selectedImagePreviewUrl);
      }

      setSelectedImageFile(processedFile);
      setSelectedImagePreviewUrl(previewUrl);
      setUploadError("");
      reduxDispatch(updateUser({ imageUrl: previewUrl }));
      showAlert("Profile image ready to save", "info");
    } catch (error) {
      setSelectedImageFile(null);
      setUploadError(error.message || "Unable to process the selected image");
      showAlert(error.message || "Unable to process the selected image", "error");
    } finally {
      setIsCompressingImage(false);
    }
  };

  const handleAddressSelect = (addressData) => {
    setValue("city", addressData.city, { shouldDirty: true, shouldValidate: tabValue === 1 });
    setValue("state", addressData.state, { shouldDirty: true, shouldValidate: tabValue === 1 });
    setValue("zipCode", addressData.zipCode, { shouldDirty: true, shouldValidate: tabValue === 1 });
    setValue("country", addressData.country, { shouldDirty: true, shouldValidate: tabValue === 1 });
    reduxDispatch(updateUser({ address: addressData.address }));
  };

  const onSubmit = async (formData) => {
    try {
      const payload = new FormData();
      const file = selectedImageFile;

      Object.entries(formData).forEach(([key, value]) => {
        if (key === "phone") {
          payload.append(key, cleanPhone(value));
        } else {
          payload.append(key, value || "");
        }
      });

      payload.append("useAliasAsDisplayName", String(displayNameMode === "alias"));

      payload.append("address", userState.address || "");

      if (file) {
        payload.append("imageFile", file);
      }

      const response = await gateway.put("/users/self", payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `${tokenLocal}`,
        },
      });

      if (response.status === 200) {
        const data = response.data.data;
        const avatarUrl = withCacheBuster(data.avatar) || selectedImagePreviewUrl || userState.avatar;
        const savedPhone = cleanPhone(formData.phone);

        reduxDispatch(
          updateUser({
            firstName: data.firstName,
            lastName: data.lastName,
            alias: data.alias ?? formData.alias ?? "",
            useAliasAsDisplayName: Boolean(data.useAliasAsDisplayName),
            displayName: data.displayName,
            avatar: avatarUrl,
            phone: data.phone ?? savedPhone,
            phoneNumber: data.phone ?? data.phoneNumber ?? savedPhone,
            address: data.address,
            countryCode: data.countryCode ?? formData.countryCode,
            country: data.country ?? formData.country,
            imageUrl: null,
          }),
        );

        reduxDispatch(
          updateActiveCompany({
            businessName: formData.companyName,
            employerId: formData.employerId,
            city: formData.city,
            state: formData.state,
            zipCode: formData.zipCode,
            country: formData.country,
            address: data.address ?? userState.address ?? "",
          }),
        );

        if (selectedImagePreviewUrl) {
          URL.revokeObjectURL(selectedImagePreviewUrl);
        }

        setSelectedImageFile(null);
        setSelectedImagePreviewUrl("");
        setUploadError("");

        await refreshToken();
        await loadUserData();

        showAlert("Profile updated successfully", "success");
      }
    } catch (error) {
      console.error("Submit error:", error);

      showAlert(error?.response?.data?.message || "Error updating profile", "error");
    }
  };

  const onInvalidSubmit = (formErrors) => {
    const orderedFieldsByTab =
      tabValue === 1 ? ["city", "state", "zipCode", "country"] : ["firstName", "lastName", "companyName", "employerId", "phone", "countryCode"];

    const firstError = orderedFieldsByTab.map((field) => formErrors[field]).find(Boolean) ?? Object.values(formErrors)[0];

    showAlert(firstError?.message || "Please review the highlighted fields before saving.", "error");
  };

  const refreshToken = async () => {
    const refreshToken = sessionStorage.getItem("refresh_token");
    try {
      const response = await refreshGateway.post("/tokens/refresh", {}, { headers: { Authorization: `${refreshToken}` } });
      const { token, refresh_token } = response.data.data;
      sessionStorage.setItem("token", token);
      sessionStorage.setItem("refresh_token", refresh_token.token);
    } catch (error) {
      console.error("Token refresh error:", error);
    }
  };

  const renderSaveButton = () => (
    <Button
      type="submit"
      variant="contained"
      disableElevation
      disabled={isSubmitting}
      sx={{
        borderRadius: "8px",
        minHeight: 30,
        px: 1.25,
        py: 0.5,
        fontSize: "0.75rem",
        fontWeight: 600,
        backgroundColor: colors.orange.base,
        textTransform: "none",
        boxShadow: "none",
        transition: "background-color 140ms ease",
        "&:hover:not(:disabled)": {
          backgroundColor: colors.orange[600],
          boxShadow: "none",
        },
        "&:disabled": {
          opacity: 0.6,
          boxShadow: "none",
        },
      }}
    >
      {isSubmitting ? (
        <>
          <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
          Saving...
        </>
      ) : (
        "Save Changes"
      )}
    </Button>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress sx={{ color: colors.orange.base }} />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: { xs: 2, md: 4 }, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Box sx={{ width: "100%" }}>
        <Card
          elevation={0}
          component="form"
          onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}
          sx={{
            overflow: "hidden",
            px: 2,
            pt: 1,
            pb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mb: 0.5,
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "0.35px",
                  textTransform: "uppercase",
                  color: colors.gray[400],
                  lineHeight: 1.2,
                }}
              >
                Update Your Information
              </Typography>
            </Box>
            {renderSaveButton()}
          </Box>

          <CardContent sx={{ padding: { xs: "10px 24px 24px", md: "12px 32px 32px" } }}>
            <Box sx={{ borderBottom: 1, borderColor: colors.gray[100], marginBottom: 3 }}>
              <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                sx={{
                  minHeight: 34,
                  "& .MuiTabs-scroller": {
                    minHeight: 34,
                  },
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    minHeight: 34,
                    py: 0.65,
                    color: colors.gray[400],
                    "&.Mui-selected": {
                      color: colors.orange.base,
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: colors.orange.base,
                  },
                }}
              >
                <Tab label="Personal & Business" />
                <Tab label="Address" />
              </Tabs>
            </Box>

            {tabValue === 0 && (
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: colors.gray[500],
                      marginBottom: "16px",
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Personal Details
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "center", sm: "flex-start" }, gap: 2.5 }}>
                    <Box sx={{ flexShrink: 0, pt: { xs: 0, sm: 0.5 }, width: { xs: "100%", sm: 116 } }}>
                      <EditableAccountAvatar
                        account={{
                          ...userState,
                          imageUrl: selectedImagePreviewUrl || userState.imageUrl,
                        }}
                        size={108}
                        isLoading={isCompressingImage}
                        onSelectAvatarFile={handleAccountAvatarFile}
                      />
                      {uploadError && (
                        <Typography sx={{ fontSize: "0.85rem", color: colors.danger.base, marginTop: "8px" }}>{uploadError}</Typography>
                      )}
                      <Typography
                        sx={{
                          mt: 2.25,
                          width: "100%",
                          color: colors.gray[400],
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          textAlign: "center",
                          whiteSpace: "normal",
                          lineHeight: 1.15,
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 2,
                          wordBreak: "normal",
                        }}
                      >
                        {displayNamePreview}
                      </Typography>
                    </Box>

                    <Grid container rowSpacing={2.5} columnSpacing={1.5} sx={{ flex: 1, width: "100%", minWidth: 0 }}>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="firstName"
                          control={control}
                          rules={{ required: tabValue === 0 ? "First name is required" : false }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="First Name"
                              variant="outlined"
                              error={!!errors.firstName}
                              helperText={errors.firstName?.message}
                              size="small"
                              sx={compactInputSx}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Controller
                              name="lastName"
                              control={control}
                              rules={{ required: tabValue === 0 ? "Last name is required" : false }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label="Last Name"
                                  variant="outlined"
                                  error={!!errors.lastName}
                                  helperText={errors.lastName?.message}
                                  size="small"
                                  sx={compactInputSx}
                                />
                              )}
                            />
                          </Box>
                          <Tooltip title="Use first and last name">
                            <Radio
                              checked={displayNameMode === "fullName"}
                              onChange={() => handleDisplayNameModeChange("fullName")}
                              size="small"
                              inputProps={{ "aria-label": "Use first and last name" }}
                              sx={{ p: 0.25, color: colors.orange.base, "&.Mui-checked": { color: colors.orange.base } }}
                            />
                          </Tooltip>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Controller
                              name="alias"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  fullWidth
                                  label="Alias"
                                  variant="outlined"
                                  size="small"
                                  sx={compactInputSx}
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            />
                          </Box>
                          <Tooltip title="Use alias">
                            <Radio
                              checked={displayNameMode === "alias"}
                              onChange={() => handleDisplayNameModeChange("alias")}
                              size="small"
                              inputProps={{ "aria-label": "Use alias" }}
                              sx={{ p: 0.25, color: colors.orange.base, "&.Mui-checked": { color: colors.orange.base } }}
                            />
                          </Tooltip>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Tooltip
                              slotProps={{ popper: { sx: { zIndex: 99999999 } } }}
                              title="Contact support to change your email: support@flownect.com"
                            >
                              <label>
                                <Controller
                                  name="email"
                                  control={control}
                                  render={({ field }) => (
                                    <TextField
                                      {...field}
                                      fullWidth
                                      label="Email"
                                      disabled
                                      variant="outlined"
                                      size="small"
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <Help sx={{ fontSize: 18, color: colors.orange.base }} />
                                          </InputAdornment>
                                        ),
                                      }}
                                      sx={compactInputSx}
                                    />
                                  )}
                                />
                              </label>
                            </Tooltip>
                          </Box>
                          <Box sx={{ width: 26, flexShrink: 0 }} />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>

                {/* <Divider sx={{ borderColor: colors.gray[100] }} />

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: colors.gray[500],
                      marginBottom: "16px",
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Business Informationdd
                  </Typography>

                  <Grid container rowSpacing={2.5} columnSpacing={2}>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="companyName"
                        control={control}
                        rules={{ required: tabValue === 0 ? "Company name is required" : false }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Company Name"
                            variant="outlined"
                            error={!!errors.companyName}
                            helperText={errors.companyName?.message}
                            size="small"
                            sx={compactInputSx}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="employerId"
                        control={control}
                        rules={{ required: tabValue === 0 ? "Employer ID is required" : false }}
                        render={({ field: { value, onChange } }) => (
                          <TextField
                            value={formatEmployeeId(value)}
                            onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ""))}
                            fullWidth
                            label="Employer ID Number"
                            variant="outlined"
                            error={!!errors.employerId}
                            helperText={errors.employerId?.message}
                            size="small"
                            inputProps={{ maxLength: 10 }}
                            sx={compactInputSx}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ borderColor: colors.gray[100] }} />
*/}

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: colors.gray[500],
                      marginBottom: "16px",
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Contact Information
                  </Typography>
                  <PhoneInputWithFlags
                    setValue={setValue}
                    control={control}
                    errors={errors}
                    name="phone"
                    countryCodeName="countryCode"
                    compact
                    required={false}
                  />
                </Box>
              </Stack>
            )}

            {tabValue === 1 && (
              <Stack spacing={3}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: colors.gray[500],
                      marginBottom: "16px",
                      fontSize: "12px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Address Information
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <AddressAutocomplete onAddressSelect={handleAddressSelect} initialValue="" compact />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="city"
                        control={control}
                        rules={{ required: tabValue === 1 ? "City is required" : false }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="City"
                            variant="outlined"
                            error={!!errors.city}
                            helperText={errors.city?.message}
                            size="small"
                            sx={compactInputSx}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="state"
                        control={control}
                        rules={{ required: tabValue === 1 ? "State is required" : false }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="State"
                            variant="outlined"
                            error={!!errors.state}
                            helperText={errors.state?.message}
                            size="small"
                            sx={compactInputSx}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Controller
                        name="zipCode"
                        control={control}
                        rules={{ required: tabValue === 1 ? "Zip code is required" : false }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Zip Code"
                            variant="outlined"
                            error={!!errors.zipCode}
                            helperText={errors.zipCode?.message}
                            size="small"
                            sx={compactInputSx}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        name="country"
                        control={control}
                        rules={{ required: tabValue === 1 ? "Country is required" : false }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Country"
                            variant="outlined"
                            error={!!errors.country}
                            helperText={errors.country?.message}
                            size="small"
                            sx={compactInputSx}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 4,
              }}
            >
              <Divider sx={{ flex: 1, borderColor: colors.gray[100] }} />
              <Typography
                variant="caption"
                sx={{
                  color: colors.danger.base,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                }}
              >
                Danger Zone
              </Typography>
              <Divider sx={{ flex: 1, borderColor: colors.gray[100] }} />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <AccountDeactivation
                buttonSx={{
                  minHeight: 30,
                  px: 2,
                  py: 1,
                  fontSize: "0.75rem",
                  mt: 2,
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
      <SnackbarAlert
        openState={alert.open}
        setOpenState={(open) => setAlert({ ...alert, open })}
        severity={alert.severity}
        duration={4500}
        message={alert.message}
      />
    </Box>
  );
};
